import type { AccessUser, Employee, RoleSummary } from '../types/api'

type WorkspaceAccess = Pick<AccessUser, 'isSuperAdmin' | 'permissions' | 'roles'>
type EmployeeAccess = Pick<Employee, 'roles'>

export function isCeo(access: Pick<WorkspaceAccess, 'roles'> | null | undefined): boolean {
  return Boolean(access?.roles.some((role) => role.code === 'CEO'))
}

export function canManageTaskReminders(access: Pick<WorkspaceAccess, 'roles'> | null | undefined): boolean {
  // Reminder policy is reserved for persisted CEO/GM roles, not permission or
  // superadmin bypasses. The reminders API independently enforces this rule.
  return Boolean(access?.roles.some((role) => role.code === 'CEO' || role.code === 'GENERAL_MANAGER'))
}

export function canViewCompanySettings(access: WorkspaceAccess | null | undefined): boolean {
  return ['settings.manage', 'finance.read', 'finance.manage'].some((permission) =>
    hasWorkspacePermission(access, permission),
  )
}

export function canOpenSettings(access: WorkspaceAccess | null | undefined): boolean {
  return canViewCompanySettings(access) || canManageTaskReminders(access)
}

export function hasFullWorkspaceAccess(access: WorkspaceAccess | null | undefined): boolean {
  return Boolean(access?.isSuperAdmin || isCeo(access))
}

export function hasWorkspacePermission(
  access: WorkspaceAccess | null | undefined,
  permission?: string,
): boolean {
  return !permission || hasFullWorkspaceAccess(access) || Boolean(access?.permissions.includes(permission))
}

export function isExecutiveUser(access: WorkspaceAccess | null | undefined): boolean {
  return hasFullWorkspaceAccess(access) || Boolean(access?.roles.some((role) => role.code === 'GENERAL_MANAGER'))
}

export function isManagementReviewer(access: WorkspaceAccess | null | undefined): boolean {
  if (isCeo(access) || access?.roles.some((role) => role.code === 'GENERAL_MANAGER')) return false
  return Boolean(access?.isSuperAdmin || access?.roles.some((role) => role.code === 'HR_ADMIN'))
}

export function canReviewSubmission(
  access: (WorkspaceAccess & Pick<AccessUser, 'id'>) | null | undefined,
  ownerId: string,
  permission: 'leave.review' | 'weekly_reports.review',
): boolean {
  return Boolean(access && access.id !== ownerId && hasWorkspacePermission(access, permission))
}

export function isCeoEmployee(employee: EmployeeAccess | null | undefined): boolean {
  return Boolean(employee?.roles.some(({ role }) => role.code === 'CEO'))
}

export function canManageEmployee(
  access: WorkspaceAccess | null | undefined,
  employee: EmployeeAccess | null | undefined,
): boolean {
  return Boolean(employee) && hasWorkspacePermission(access, 'employees.manage') &&
    (!isCeoEmployee(employee) || isCeo(access))
}

export function canManageEmployeeAccess(
  access: WorkspaceAccess | null | undefined,
  employee: EmployeeAccess | null | undefined,
): boolean {
  return Boolean(employee) && hasWorkspacePermission(access, 'roles.manage') &&
    (!isCeoEmployee(employee) || isCeo(access))
}

export function canAssignRole(
  access: WorkspaceAccess | null | undefined,
  role: Pick<RoleSummary, 'code'>,
): boolean {
  return hasWorkspacePermission(access, 'roles.manage') && (role.code !== 'CEO' || isCeo(access))
}

export function canRemoveRole(
  access: WorkspaceAccess | null | undefined,
  employee: EmployeeAccess | null | undefined,
  role: Pick<RoleSummary, 'code'>,
): boolean {
  return role.code !== 'EMPLOYEE' && canManageEmployeeAccess(access, employee) && canAssignRole(access, role)
}

export function canSetEmployeeOverride(
  access: WorkspaceAccess | null | undefined,
  employee: EmployeeAccess | null | undefined,
): boolean {
  // Full CEO access is immutable; allow cleaning up stale overrides separately.
  return canManageEmployeeAccess(access, employee) && !isCeoEmployee(employee)
}

export type AdministrationSection = 'assets' | 'procurement' | 'attendance'

/** Match the administration endpoints; a write grant does not imply list access. */
export function administrationCapabilities(access: WorkspaceAccess | null | undefined) {
  const can = (permission: string) => hasWorkspacePermission(access, permission)
  const canReadAssets = can('admin.assets.read')
  const canManageAssets = can('admin.assets.manage')
  const canCreateProcurement = can('admin.procurement.own.manage')
  const canReviewProcurement = can('admin.procurement.review')
  const canReadAllProcurement = can('admin.procurement.read')
  const canReadProcurement = canCreateProcurement || canReadAllProcurement || canReviewProcurement
  const canReadAttendance = can('attendance.read') || can('attendance.manage')
  const canClockAttendance = can('attendance.own.manage')
  const canViewAttendance = canReadAttendance || canClockAttendance
  const sections: AdministrationSection[] = [
    ...(canReadAssets ? ['assets' as const] : []),
    ...(canReadProcurement ? ['procurement' as const] : []),
    ...(canViewAttendance ? ['attendance' as const] : []),
  ]
  return {
    canReadAssets,
    canManageAssets,
    canReadProcurement,
    canReadAllProcurement,
    canCreateProcurement,
    canReviewProcurement,
    canReadAttendance,
    canClockAttendance,
    canViewAttendance,
    canReadAssetAssignees: canManageAssets && can('employees.read'),
    sections,
    defaultSection: canReadProcurement ? 'procurement' as const : sections[0] ?? null,
  }
}
